import React from 'react';
import { Label, Stack, Toggle } from '@fluentui/react';
import { ILintResult } from './searchPageConfigLint';

// Reusable "Raw JSON" style-config editor. Given the current JSON string, a linter and an
// onChange, it renders a toggle + validated textarea (mirrors the Search page editor's raw
// mode). On every valid edit it re-serializes compactly and calls onChange; invalid text is
// kept in the box with inline errors but not pushed upstream. Used for headerConfig /
// footerConfig (and anything else that's a bag of CSS style sections).
interface IRawJsonConfigEditorProps {
    label: string;
    value?: string;
    lint: (raw: string) => ILintResult;
    onChange: (json: string) => void;
    keysHelp: React.ReactNode;
}

const RawJsonConfigEditor: React.FC<IRawJsonConfigEditorProps> = ({ label, value, lint, onChange, keysHelp }) => {
    const [open, setOpen] = React.useState(false);
    const [text, setText] = React.useState('');
    const [result, setResult] = React.useState<ILintResult | null>(null);

    const enter = () => {
        let pretty = value ?? '';
        try { if (value) pretty = JSON.stringify(JSON.parse(value), null, 2); } catch { /* keep raw */ }
        setText(pretty);
        setResult(null);
        setOpen(true);
    };

    const onText = (t: string) => {
        setText(t);
        const r = lint(t);
        setResult(r);
        if (r.valid) {
            try { onChange(JSON.stringify(JSON.parse(t))); } catch { /* lint already reported */ }
        }
    };

    return (
        <div style={{ marginBottom: '1em' }}>
            <Stack horizontal horizontalAlign='space-between' verticalAlign='center' style={{ marginBottom: 8 }}>
                <Label>{label}</Label>
                <Toggle inlineLabel label='Raw JSON' checked={open}
                    onChange={(_, checked) => (checked ? enter() : setOpen(false))}
                    styles={{ root: { marginBottom: 0 } }} />
            </Stack>
            {open && (
                <div style={{ padding: '1em', border: '1px solid #edebe9', borderRadius: 4, background: '#faf9f8' }}>
                    <textarea
                        value={text}
                        onChange={(e) => onText(e.target.value)}
                        spellCheck={false}
                        aria-label={`${label} JSON`}
                        style={{ width: '100%', minHeight: 180, boxSizing: 'border-box', fontFamily: 'Consolas, "Courier New", monospace', fontSize: 13, lineHeight: 1.5, padding: 8, borderRadius: 4, resize: 'vertical', border: `1px solid ${result && !result.valid ? '#d13438' : '#8a8886'}` }}
                    />
                    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {result?.errors.map((e, i) => (
                            <span key={`e${i}`} style={{ color: '#d13438', fontSize: 12 }}>• {e}</span>
                        ))}
                        {result?.warnings.map((w, i) => (
                            <span key={`w${i}`} style={{ color: '#797673', fontSize: 12 }}>• {w}</span>
                        ))}
                        <span style={{ color: '#797673', fontSize: 12, marginTop: 4 }}>{keysHelp}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RawJsonConfigEditor;
